/** @format */

import {useMemo} from 'react';

import {
  addMonths,
  endOfMonth,
  format,
  getDate,
  isAfter,
  isBefore,
  isEqual,
  startOfMonth,
  startOfToday,
  subDays,
  subMonths,
} from 'date-fns';
import {ru} from 'date-fns/locale';

import {getSpentSum} from 'pages/Home/Main/Project/components/BudgetAndExpese/components/ExpenditureWidget/helpers/getSpentSum';

import {useBudgetWidgetsData} from '../../../hooks/BudgetWidgetsDataContext';
import {useGetStatsQuery} from '../graphql/__generated__';
import {ExpenditureType} from '../interfaces';

export interface ExpenditureStats {
  type: ExpenditureType;
  spent: number;
  plan: number;
  currency: string;
  startDate?: string;
  endDate?: string;
  startMonth?: string;
  endMonth?: string;
  startDateFull?: Date;
  endDateFull?: Date;
  currentMonth?: string;
  avgPerDay?: number;
  daysInMonth?: number;
  prevMonthSpent?: number;
  difference?: number;
  percentageChange?: number;
  isGrowth?: boolean;
}

export interface ExpenditureReturn {
  result: ExpenditureStats;
  error: boolean;
  loading: boolean;
}

export const useExpenditure = (): ExpenditureReturn => {
  const yesterday = useMemo(() => {
    const today = startOfToday();
    const todayString = format(today, 'yyyy-MM-dd');
    const yesterdayDate = subDays(new Date(todayString), 1);
    return yesterdayDate;
  }, []);

  const budgetWidgetsData = useBudgetWidgetsData();
  const contextCompany = budgetWidgetsData?.data?.currentUser?.currentCompany;
  const mediaplans = contextCompany?.unfinishedMediaplans || [];
  const isMediaplansLoading = budgetWidgetsData?.loading;

  const activeMediaplan = mediaplans?.find(plan => {
    const startDate = new Date(plan.startOn);

    if (plan.type === 'REPEATABLE') {
      return isBefore(startDate, yesterday) || isEqual(startDate, yesterday);
    }

    const endDate = plan.endOn ? new Date(plan.endOn) : undefined;

    if (!endDate) return;

    return (
      (isBefore(startDate, yesterday) || isEqual(startDate, yesterday)) &&
      (isAfter(endDate, yesterday) || isEqual(endDate, yesterday))
    );
  });

  const statsDateRange = useMemo(() => {
    const today = startOfToday();
    const dayOfMonth = getDate(today);

    if (activeMediaplan) {
      return {
        startDate: activeMediaplan.startOn,
        endDate: activeMediaplan.endOn || format(today, 'yyyy-MM-dd'),
      };
    }

    let targetMonth: Date;

    if (dayOfMonth === 1) {
      const prevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      targetMonth = prevMonth;
    } else {
      targetMonth = today;
    }

    return {
      startDate: format(startOfMonth(targetMonth), 'yyyy-MM-dd'),
      endDate: format(endOfMonth(targetMonth), 'yyyy-MM-dd'),
    };
  }, [activeMediaplan]);

  const prevMonthDateRange = useMemo(() => {
    if (activeMediaplan) {
      return undefined;
    }

    const today = startOfToday();
    const dayOfMonth = getDate(today);

    let targetMonth: Date;
    let prevMonth: Date;

    if (dayOfMonth === 1) {
      targetMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      prevMonth = subMonths(targetMonth, 1);
    } else {
      targetMonth = today;
      prevMonth = subMonths(targetMonth, 1);
    }

    return {
      startDate: format(startOfMonth(prevMonth), 'yyyy-MM-dd'),
      endDate: format(endOfMonth(prevMonth), 'yyyy-MM-dd'),
    };
  }, [activeMediaplan]);

  const {
    data: statsData,
    loading: statsLoading,
    error: statsError,
  } = useGetStatsQuery({
    variables: statsDateRange,
    skip: isMediaplansLoading,
  });

  const {
    data: prevMonthStatsData,
    loading: prevMonthStatsLoading,
    error: prevMonthStatsError,
  } = useGetStatsQuery({
    variables: prevMonthDateRange,
    skip: isMediaplansLoading || !prevMonthDateRange,
  });

  const result: ExpenditureStats = useMemo(() => {
    const stats = statsData?.currentUser?.currentCompany?.graphStats;
    const prevMonthStats = prevMonthStatsData?.currentUser?.currentCompany?.graphStats;

    if (activeMediaplan) {
      const startDate = new Date(activeMediaplan.startOn);
      const today = startOfToday();

      // Если медиаплан привязан к началу периода, то граница периода — ближайшая дата
      // с днём месяца, совпадающим с днём старта медиаплана (например, старт 3-го → граница 3-е).
      // При этом учитываем разные длины месяцев: если текущий месяц короче, берём последний день месяца.
      const startDayOfMonth = getDate(startDate);
      const daysInThisMonth = endOfMonth(today).getDate();
      const boundaryDayThisMonth = Math.min(startDayOfMonth, daysInThisMonth);
      const thisMonthBoundary = new Date(
        today.getFullYear(),
        today.getMonth(),
        boundaryDayThisMonth,
      );
      const nextMonth = addMonths(new Date(today.getFullYear(), today.getMonth(), 1), 1);
      const daysInNextMonth = endOfMonth(nextMonth).getDate();
      const boundaryDayNextMonth = Math.min(startDayOfMonth, daysInNextMonth);
      const nextMonthBoundary = new Date(
        nextMonth.getFullYear(),
        nextMonth.getMonth(),
        boundaryDayNextMonth,
      );
      const nextPeriodBoundary = isAfter(thisMonthBoundary, today)
        ? thisMonthBoundary
        : nextMonthBoundary;

      // Иначе берём конец текущего месяца
      const endDateWithoutMediaplanEndOn = activeMediaplan.startOnPeriodBegin
        ? endOfMonth(today)
        : nextPeriodBoundary;

      const endDate = activeMediaplan.endOn
        ? new Date(activeMediaplan.endOn)
        : endDateWithoutMediaplanEndOn;

      const totalSpent = getSpentSum(stats);
      const currency = stats?.find(stat => stat?.currency)?.currency || 'RUB';

      return {
        type: 'mediaplan',
        spent: totalSpent,
        plan: activeMediaplan.budget || 0,
        currency,
        startDate: format(startDate, 'dd.MM'),
        endDate: format(endDate, 'dd.MM'),
        startMonth: format(startDate, 'LLLL', {locale: ru}),
        endMonth: format(endDate, 'LLLL', {locale: ru}),
        startDateFull: startDate,
        endDateFull: endDate,
      };
    }

    const currentMonth = format(yesterday, 'LLLL', {locale: ru});
    const daysInMonth = yesterday.getDate();

    const monthSpent = getSpentSum(stats);
    const prevMonthSpent = getSpentSum(prevMonthStats);
    const currency = stats?.find(stat => stat.currency)?.currency || 'RUB';
    const avgPerDay = daysInMonth > 0 ? monthSpent / daysInMonth : 0;
    const difference = monthSpent - prevMonthSpent;

    const percentageChange =
      prevMonthSpent > 0
        ? ((monthSpent - prevMonthSpent) / prevMonthSpent) * 100
        : monthSpent > 0
          ? 100
          : 0;

    return {
      type: 'month',
      spent: monthSpent,
      plan: 0,
      currency,
      currentMonth,
      avgPerDay,
      daysInMonth,
      prevMonthSpent,
      difference: Math.abs(difference),
      percentageChange,
      isGrowth: difference > 0,
    };
  }, [
    statsData?.currentUser?.currentCompany?.graphStats,
    prevMonthStatsData?.currentUser?.currentCompany?.graphStats,
    activeMediaplan,
    yesterday,
  ]);

  return {
    result,
    loading: statsLoading || isMediaplansLoading || prevMonthStatsLoading,
    error: Boolean(statsError || budgetWidgetsData?.error || prevMonthStatsError),
  };
};
